import { CornerUpLeft, Trash2 } from 'lucide-react'
import { CHAT_COPY, type ChatMessage, type DeliveryState } from '@/entities/chat'
import { cn } from '@/shared/lib/utils'
import { DeliveryTicks } from './DeliveryTicks'
import { AttachmentTiles } from './AttachmentTiles'
import { PersonAvatar } from './PersonAvatar'

type MessageBubbleProps = {
  conversationId: number
  message: ChatMessage
  mine: boolean
  time: string
  deliveryState: DeliveryState | null
  quotedText: string | null
  error?: string | null
  showRoleTag: boolean
  senderAvatar?: { photoUrl: string | null; photoFileId: string | null } | null
  onReply?: () => void
  onDelete?: () => void
  onRetry?: () => void
  onDiscard?: () => void
  onShowInfo?: () => void
}

export function MessageBubble({
  conversationId,
  message,
  mine,
  time,
  deliveryState,
  quotedText,
  error,
  showRoleTag,
  senderAvatar,
  onReply,
  onDelete,
  onRetry,
  onDiscard,
  onShowInfo,
}: MessageBubbleProps) {
  if (message.isDeleted) {
    return (
      <div className={cn('group flex px-3 py-0.5', mine ? 'justify-end' : 'justify-start')}>
        <div className="max-w-[78%] rounded-[10px] border border-dashed border-line px-2.5 py-1.5 text-[13px] italic text-ink-4">
          Message deleted
          <span className="ml-2 font-mono text-[11px] not-italic tabular-nums">{time}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('group flex px-3 py-0.5', mine ? 'justify-end' : 'justify-start')}>
      <div className="flex max-w-[78%] items-start gap-1">
        {!mine && senderAvatar && (
          <PersonAvatar
            name={message.senderName}
            photoUrl={senderAvatar.photoUrl}
            photoFileId={senderAvatar.photoFileId}
            size="sm"
            className="mt-0.5"
          />
        )}
        {mine && (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            {onReply && (
              <button type="button" onClick={onReply} aria-label="Reply" className="rounded-md p-1 text-ink-4 hover:bg-hover hover:text-ink-2">
                <CornerUpLeft size={13} />
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={onDelete} aria-label="Delete" className="rounded-md p-1 text-ink-4 hover:bg-hover hover:text-bad">
                <Trash2 size={13} />
              </button>
            )}
          </div>
        )}
        <div
          className={cn(
            'min-w-0 rounded-[10px] px-2.5 py-1.5 text-[13px] leading-snug',
            mine ? 'bg-brand-accent text-white' : 'bg-surface-mute text-ink',
          )}
        >
          {!mine && (
            <div className="mb-0.5 text-[11.5px] font-semibold text-ink-3">
              {message.senderName}
              {showRoleTag && message.senderRole === 'Staff' && (
                <span className="ml-1.5 font-mono text-[10px] text-ink-4">OPS</span>
              )}
            </div>
          )}
          {quotedText && (
            <div className={cn('mb-1 break-words rounded border-l-2 px-2 py-1 text-[12px]', mine ? 'border-white/50 bg-white/15 text-white/85' : 'border-line-strong bg-surface text-ink-3')}>
              {quotedText}
            </div>
          )}
          {message.content?.body && <div className="whitespace-pre-wrap break-words">{message.content.body}</div>}
          {message.content && message.content.attachments.length > 0 && (
            <AttachmentTiles conversationId={conversationId} attachments={message.content.attachments} />
          )}
          <div className={cn('mt-0.5 flex items-center justify-end text-[10.5px]', mine ? 'text-white/70' : 'text-ink-4')}>
            <span className="font-mono tabular-nums">{time}</span>
          </div>
        </div>
        {deliveryState && (
          <div className="self-end pb-0.5">
            <DeliveryTicks state={deliveryState} onClick={onShowInfo} />
          </div>
        )}
        {!mine && (onReply || onDelete) && (
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            {onReply && (
              <button type="button" onClick={onReply} aria-label="Reply" className="rounded-md p-1 text-ink-4 hover:bg-hover hover:text-ink-2">
                <CornerUpLeft size={13} />
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={onDelete} aria-label="Delete" className="rounded-md p-1 text-ink-4 hover:bg-hover hover:text-bad">
                <Trash2 size={13} />
              </button>
            )}
          </div>
        )}
      </div>
      {deliveryState === 'failed' && (
        <div className="mt-0.5 flex items-center gap-2 text-[11.5px] text-bad">
          <button type="button" onClick={onRetry} className="underline underline-offset-2">
            {error ?? CHAT_COPY.failed}
          </button>
          <button type="button" onClick={onDiscard} className="text-ink-4 underline underline-offset-2">
            {CHAT_COPY.discard}
          </button>
        </div>
      )}
    </div>
  )
}
